import { useId, useRef } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { dropFiles } from '../../../Services/OrderWizard/dropFiles.ts'
import type { DropTarget } from '../../../Services/OrderWizard/dropTargets.ts'
import { dropDisabled, dropPoolFor, dropTargets } from '../../../Services/OrderWizard/dropTargets.ts'
import { DROPZONE_ACCEPT } from '../../../Services/OrderWizard/wizardFiles.ts'
import { DropZones, targetId } from './DropZones.tsx'
import { useFilePicker } from './useFilePicker.tsx'
import { useAddFiles } from './AddFilesContext.ts'
import { useDismissable } from '../../../Framework/View/Hooks/useDismissable.ts'
import { useFocusOnMount } from '../../../Framework/View/Hooks/useFocusOnMount.ts'

/* zones that still share the popover's height; more scroll */
const POPOVER_FILL = 6

const AddFilesPanel = ({ draft, id, onDone }: { draft: DraftState; id: string; onDone: () => void }) => {
  const dispatch = useDispatch()
  const panel = useRef<HTMLDivElement>(null)
  const picking = useRef<DropTarget>(undefined)
  const targets = dropTargets(draft)
  useFocusOnMount(() => panel.current?.querySelector<HTMLElement>(`[data-drop-target="${targetId(targets[0])}"]`))
  const take = (target: DropTarget, files: File[]) => {
    dropFiles(draft.id, files, dispatch, dropPoolFor(draft, target))
    onDone()
  }
  const picker = useFilePicker({
    accept: DROPZONE_ACCEPT,
    testId: 'wizard-add-files-input',
    onFiles: (files) => picking.current && take(picking.current, files)
  })
  return (
    <div
      ref={panel}
      id={id}
      role="dialog"
      aria-label="Add files"
      className="absolute right-0 z-40 mt-1 w-[26rem] max-w-[calc(100vw-2rem)] origin-top-right space-y-2 rounded-box border border-slate-200 bg-white p-3 shadow-lg transition-[opacity,transform] duration-150 ease-out starting:scale-95 starting:opacity-0 motion-reduce:transition-none"
      data-testid="wizard-add-files-popover"
    >
      {targets.length > 1 && <p className="text-xs text-slate-500">Drop files on a pool, or click one to choose files for it.</p>}
      <DropZones
        testId="wizard-add-files-zone"
        variant="popover"
        targets={targets}
        disabled={dropDisabled(draft)}
        onFiles={take}
        onPick={(target) => {
          picking.current = target
          picker.open()
        }}
        className={
          targets.length <= 3
            ? 'h-28 flex-row gap-2'
            : targets.length <= POPOVER_FILL
              ? 'h-72 flex-col gap-1.5'
              : 'max-h-72 flex-col gap-1.5 overflow-y-auto'
        }
        zoneClassName={targets.length > POPOVER_FILL ? 'min-h-12 min-w-0 shrink-0' : undefined}
      />
      <p className="text-xs text-slate-500">FASTQ files and a sample sheet; a sheet applies to the whole order.</p>
      {picker.input}
    </div>
  )
}

/** The header's "Add files": a popover with a drop zone per pool, each also a button to choose files for it. */
export const AddFilesButton = ({ draft }: { draft: DraftState }) => {
  const { request, open, close } = useAddFiles()
  const id = useId()
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const isOpen = !!request

  useDismissable(root, isOpen, (reason) => {
    close()
    if (reason === 'escape') {
      button.current?.focus()
    }
  })

  const done = () => {
    close()
    button.current?.focus()
  }

  return (
    <div ref={root} className="relative shrink-0">
      <button
        ref={button}
        type="button"
        className="btn btn-ghost btn-sm gap-1.5 font-normal text-slate-700"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        data-testid="wizard-add-files"
        onClick={() => (isOpen ? close() : open())}
      >
        <ArrowUpTrayIcon className="h-4 w-4" aria-hidden />
        Add files
      </button>
      {request && <AddFilesPanel key={request.seq} draft={draft} id={id} onDone={done} />}
    </div>
  )
}
